import {
	AlertTriangle,
	File06,
	Lock01,
	ShieldTick,
	User01,
} from "@untitledui/icons";
import type { ReactNode } from "react";
import { ConfirmationModal } from "@/components/Organisms/ConfirmationModal/ConfirmationModal";
import { useAppTranslation } from "@/hooks/use-app-translation";

export interface DeleteAccountDialogProps {
	isOpen: boolean;
	isLoading: boolean;
	showEntraCopy: boolean;
	hasError: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

function ConsequenceRow({
	icon,
	title,
	body,
}: {
	icon: ReactNode;
	title: string;
	body: string;
}) {
	return (
		<div className="flex items-start gap-4 py-1 text-left">
			<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-tertiary text-fg-quaternary">
				{icon}
			</div>
			<div className="min-w-0">
				<p className="text-sm font-extrabold text-primary">{title}</p>
				<p className="text-xs font-medium text-primary">{body}</p>
			</div>
		</div>
	);
}

export function DeleteAccountDialog({
	isOpen,
	isLoading,
	showEntraCopy,
	hasError,
	onCancel,
	onConfirm,
}: DeleteAccountDialogProps) {
	const { t } = useAppTranslation();
	const showError = hasError && !isLoading;

	return (
		<ConfirmationModal
			isOpen={isOpen}
			setIsOpen={(open) => {
				if (!open && !isLoading) onCancel();
			}}
			title={
				showError
					? t("userProfile.deleteAccount.error")
					: t("userProfile.deleteAccount.title")
			}
			icon={({ className }) => (
				<AlertTriangle className={`${className ?? ""} text-white`} />
			)}
			iconBgColor="bg-error-600"
			confirmText={
				isLoading
					? t("userProfile.deleteAccount.loading")
					: showError
						? t("userProfile.deleteAccount.tryAgain")
						: t("common.actions.delete")
			}
			cancelText={t("common.actions.cancel")}
			confirmColor="primary-destructive"
			isLoading={isLoading}
			onCancel={onCancel}
			onConfirm={onConfirm}
			confirmTestId="delete-account-confirm"
			description={
				showError ? (
					<p className="text-center text-md text-secondary">
						{t("userProfile.deleteAccount.errorBody")}
					</p>
				) : (
					<div className="flex flex-col gap-4">
						<p className="text-center text-md text-secondary">
							{t("userProfile.deleteAccount.cannotUndo")}
						</p>
						<div className="flex flex-col gap-2">
							<ConsequenceRow
								icon={<User01 className="size-5" />}
								title={t("userProfile.deleteAccount.accountTitle")}
								body={t("userProfile.deleteAccount.accountBody")}
							/>
							<ConsequenceRow
								icon={<Lock01 className="size-5" />}
								title={t("userProfile.deleteAccount.accessTitle")}
								body={t("userProfile.deleteAccount.accessBody")}
							/>
							<ConsequenceRow
								icon={<File06 className="size-5" />}
								title={t("userProfile.deleteAccount.recordsTitle")}
								body={t("userProfile.deleteAccount.recordsBody")}
							/>
							{showEntraCopy ? (
								<ConsequenceRow
									icon={<ShieldTick className="size-5" />}
									title={t("userProfile.deleteAccount.entraTitle")}
									body={t("userProfile.deleteAccount.entraBody")}
								/>
							) : null}
						</div>
					</div>
				)
			}
		/>
	);
}
